import type { Dispatch, MiddlewareAPI } from '@reduxjs/toolkit';

import {
  beginReconnect,
  broken,
  closed,
  error,
  message,
  open,
  reconnectAttempt,
  reconnected,
} from './actions';
import type { Action, Serializer, Deserializer, WsConnection } from './types';

export interface ReduxWebSocketOptions {
  prefix: string;
  /**
   * Mutual-exclusion group. Channels sharing the same non-empty group
   * force-disconnect each other on connect; channels with no group coexist
   * with every other channel. See {@link Options.exclusionGroup}.
   */
  exclusionGroup?: string;
  reconnectInterval: number;
  reconnectOnClose: boolean;
  reconnectOnError: boolean;
  onOpen?: (s: WebSocket) => void;
  serializer?: Serializer;
  deserializer?: Deserializer;
}

/**
 * Handle a message event.
 *
 * @param {Dispatch} dispatch
 * @param {string} prefix
 * @param {Deserializer} deserializer
 * @param {MessageEvent} event
 */
function handleMessage(
  dispatch: Dispatch,
  prefix: string,
  deserializer: Deserializer | undefined,
  event: MessageEvent,
) {
  dispatch(message(event, prefix, deserializer));
}

/**
 * ReduxWebSocket
 * @class
 *
 * Manages a WebSocket connection.
 */
export default class ReduxWebSocket {
  private static activeConnections = new Map<string, ReduxWebSocket>();
  // Class options.
  private readonly options: ReduxWebSocketOptions;

  // WebSocket connection.
  private websocket: WebSocket | null = null;

  // Keep track of how many times we've attempted to reconnect.
  private reconnectCount = 0;

  // We'll create an interval to try and reconnect if the socket connection breaks.
  private reconnectionInterval: ReturnType<typeof setInterval> | null = null;

  // Keep track of the last connect payload we used to connect, so that when we automatically
  // try to reconnect, we can reuse the previous connect payload.
  private lastConnectPayload: WsConnection | null = null;

  // Keep track of if the WebSocket connection has ever successfully opened.
  private hasOpened = false;
  // Existing properties...
  private visibilityController: AbortController | null = null;
  // Network status tracking
  private networkController: AbortController | null = null;
  private isOnline = navigator.onLine;
  // Queue for messages that failed to send while offline
  private messageQueue: Record<string, unknown> | null = null;

  // Runtime guard to check if a value is a plain record
  private static isRecord = (val: unknown): val is Record<string, unknown> =>
    typeof val === 'object' && val !== null && !Array.isArray(val);

  /**
   * Constructor
   * @constructor
   *
   * @param {ReduxWebSocketOptions} options
   */
  constructor(options: ReduxWebSocketOptions) {
    this.options = options;
    this.initializeNetworkListener();
  }

  /**
   * Initialize network status listeners
   * @private
   */
  private initializeNetworkListener = () => {
    this.networkController = new AbortController();

    const handleOnline = () => {
      this.isOnline = true;
      this.processMessageQueue(); // Process queued messages when back online
    };

    const handleOffline = () => {
      this.isOnline = false;
      // Pause reconnection attempts when offline
      if (this.reconnectionInterval) {
        clearInterval(this.reconnectionInterval);
        this.reconnectionInterval = null;
      }
    };

    window.addEventListener('online', handleOnline, {
      signal: this.networkController.signal,
    });

    window.addEventListener('offline', handleOffline, {
      signal: this.networkController.signal,
    });
  };

  /**
   * Remove network status listeners
   * @private
   */
  private removeNetworkListener = () => {
    if (this.networkController) {
      this.networkController.abort();
      this.networkController = null;
    }
  };

  /**
   * WebSocket connect event handler.
   *
   * @param {MiddlewareAPI} store
   * @param {Action} action
   */
  connect = ({ dispatch }: MiddlewareAPI, { payload }: { payload: WsConnection }) => {
    const { prefix, exclusionGroup, deserializer } = this.options;

    // Channels coexist by default (mm/es/assistant/auth run concurrently), so
    // connecting must NOT tear down unrelated prefixes — otherwise a
    // later-connecting channel would silently evict an earlier one and its
    // `send()` frames (e.g. the assistant `Auth` command) would be dropped by
    // the guard in `send`. Only channels sharing the same non-empty
    // `exclusionGroup` are mutually exclusive (the MM and ES data channels),
    // and connecting one disconnects the other in that group.
    if (exclusionGroup) {
      ReduxWebSocket.activeConnections.forEach((connection, connectionPrefix) => {
        if (
          connectionPrefix !== prefix &&
          connection.websocket &&
          connection.options.exclusionGroup === exclusionGroup
        ) {
          console.warn(`Disconnecting WebSocket with prefix: ${connectionPrefix}`);
          connection.disconnect();
        }
      });
    }

    ReduxWebSocket.activeConnections.set(prefix, this);

    this.lastConnectPayload = payload;

    this.websocket = payload.protocols
      ? new WebSocket(payload.url, payload.protocols)
      : new WebSocket(payload.url);

    this.websocket.addEventListener('close', (event) => {
      this.handleClose(dispatch, prefix, event);
    });
    this.websocket.addEventListener('error', () => {
      this.handleError(dispatch, prefix);
    });
    this.websocket.addEventListener('open', (event) => {
      this.handleOpen(dispatch, prefix, this.options.onOpen, event);
    });
    this.websocket.addEventListener('message', (event) => {
      handleMessage(dispatch, prefix, deserializer, event);
    });
  };

  /**
   * WebSocket disconnect event handler.
   *
   * @throws {Error} Socket connection must exist.
   */
  disconnect = () => {
    this.removeVisibilityListener(); // Clean up visibility listener if it exists
    this.removeNetworkListener(); // Clean up network listener if it exists

    const { prefix } = this.options;

    ReduxWebSocket.activeConnections.delete(prefix);

    if (this.websocket) {
      this.close(1000, 'WebSocket connection closed by redux-websocket.');
    } else {
      throw new Error('Socket connection not initialized. Dispatch WEBSOCKET_CONNECT first');
    }
  };

  /**
   * Process queued messages when connection is restored
   * @private
   */
  private processMessageQueue = () => {
    if (!this.isOnline || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      return;
    }

    if (this.messageQueue) {
      if (this.options.serializer) {
        try {
          this.websocket.send(this.options.serializer(this.messageQueue));
          // Clear the entire queue after sending the latest message
          this.messageQueue = null;
        } catch (error) {
          console.error('Failed to send latest queued message:', error);
        }
      }
    }
  };

  /**
   * WebSocket send event handler.
   *
   * @param {MiddlewareAPI} _store
   * @param {Action} action
   *
   * @throws {Error} Socket connection must exist.
   */
  send = (_store: MiddlewareAPI, action: Action) => {
    const { prefix } = this.options;

    const activeConnection = ReduxWebSocket.activeConnections.get(prefix);
    if (activeConnection !== this) {
      console.warn(`Message ignored: WebSocket with prefix ${prefix} is not active`);
      return;
    }

    // Safely extract payload from action using runtime checks
    let payload: unknown;
    const unknownAction = action as unknown;
    if (typeof unknownAction === 'object' && unknownAction !== null && 'payload' in unknownAction) {
      payload = (unknownAction as Record<string, unknown>)['payload'];
    } else {
      payload = undefined;
    }

    if (this.isOnline) {
      if (this.websocket) {
        if (this.options.serializer) {
          const payloadObj: Record<string, unknown> = ReduxWebSocket.isRecord(payload)
            ? payload
            : { data: payload };

          this.websocket.send(this.options.serializer(payloadObj));
        } else {
          throw new Error('Serializer not provided');
        }
      } else {
        throw new Error('Socket connection not initialized. Dispatch WEBSOCKET_CONNECT first');
      }
    } else {
      // Queue message for later sending when connection is restored
      const payloadObj: Record<string, unknown> = ReduxWebSocket.isRecord(payload)
        ? payload
        : { data: payload };
      this.messageQueue = payloadObj;
    }
  };

  /**
   * Handle a close event.
   *
   * @param {Dispatch} dispatch
   * @param {string} prefix
   * @param {Event} event
   */
  private handleClose = (dispatch: Dispatch, prefix: string, event: CloseEvent) => {
    this.removeVisibilityListener();
    ReduxWebSocket.activeConnections.delete(prefix);

    dispatch(closed(event, prefix));

    // Clean up any existing visibility listener
    // Conditionally attempt reconnection if enabled and applicable
    if (
      this.canAttemptReconnect() &&
      !event.wasClean &&
      this.websocket &&
      this.websocket.readyState !== WebSocket.OPEN
    ) {
      this.addVisibilityListener(dispatch);
    }

    const { reconnectOnClose } = this.options;
    if (reconnectOnClose && this.canAttemptReconnect() && !event.wasClean) {
      this.handleBrokenConnection(dispatch);
    }
  };

  private addVisibilityListener = (dispatch: Dispatch) => {
    this.visibilityController = new AbortController();

    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.removeVisibilityListener();
        if (this.websocket && this.websocket.readyState !== WebSocket.OPEN) {
          this.handleBrokenConnection(dispatch);
        }
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler, {
      signal: this.visibilityController.signal,
    });
  };

  /**
   * Remove visibility change listener
   * @private
   */
  private removeVisibilityListener = () => {
    if (this.visibilityController) {
      this.visibilityController.abort();
      this.visibilityController = null;
    }
  };

  /**
   * Handle an error event.
   *
   * @param {Dispatch} dispatch
   * @param {string} prefix
   */
  private handleError = (dispatch: Dispatch, prefix: string) => {
    dispatch(error(null, new Error('`redux-websocket` error'), prefix));

    // Conditionally attempt reconnection if enabled and applicable
    const { reconnectOnError } = this.options;
    if (reconnectOnError && this.canAttemptReconnect()) {
      this.handleBrokenConnection(dispatch);
    }
  };

  /**
   * Handle an open event.
   *
   * @param {Dispatch} dispatch
   * @param {string} prefix
   * @param {(s: WebSocket) => void | undefined} onOpen
   * @param {Event} event
   */
  private handleOpen = (
    dispatch: Dispatch,
    prefix: string,
    onOpen: ((s: WebSocket) => void) | undefined,
    event: Event,
  ) => {
    // Clean up any outstanding reconnection attempts.
    if (this.reconnectionInterval) {
      clearInterval(this.reconnectionInterval);

      this.reconnectionInterval = null;
      this.reconnectCount = 0;

      dispatch(reconnected(prefix));
    }

    // Hook to allow consumers to get access to the raw socket.
    if (onOpen && this.websocket != null) {
      onOpen(this.websocket);
    }

    // Now we're fully open and ready to send messages.
    dispatch(open(event, prefix));

    // Track that we've been able to open the connection. We can use this flag
    // for error handling later, ensuring we don't try to reconnect when a
    // connection was never able to open in the first place.
    this.hasOpened = true;
  };

  /**
   * Close the WebSocket connection.
   * @private
   *
   * @param {number} [code]
   * @param {string} [reason]
   */
  private close = (code?: number, reason?: string) => {
    if (this.websocket) {
      if (this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.close(
          code ?? 1000,
          reason ?? 'WebSocket connection closed by redux-websocket.',
        );
      }

      this.websocket = null;
      this.hasOpened = false;
    }
  };

  /**
   * Handle a broken socket connection.
   * @private
   *
   * @param {Dispatch} dispatch
   */
  private handleBrokenConnection = (dispatch: Dispatch) => {
    const { prefix, reconnectInterval } = this.options;

    this.websocket = null;

    // First, dispatch actions to notify Redux that our connection broke.
    dispatch(broken(prefix));
    dispatch(beginReconnect(prefix));

    this.reconnectCount = 0;

    // Attempt reconnecting on an interval.
    this.reconnectionInterval = setInterval(() => {
      this.reconnectCount += 1;

      dispatch(reconnectAttempt(this.reconnectCount, prefix));

      if (this.lastConnectPayload !== null) {
        // Call connect again, same way.
        this.connect({ dispatch, getState: () => ({}) } satisfies MiddlewareAPI, {
          payload: this.lastConnectPayload,
        });
      }
    }, reconnectInterval);
  };

  // Only attempt to reconnect if the connection has ever successfully opened,
  // and we're not currently trying to reconnect.
  //
  // This prevents ongoing reconnect loops to connections that have not
  // successfully opened before, such as net::ERR_CONNECTION_REFUSED errors.
  //
  // This also prevents starting multiple reconnection attempt loops.
  private canAttemptReconnect(): boolean {
    return this.hasOpened && this.reconnectionInterval == null;
  }
}
